import React, { forwardRef, useImperativeHandle } from "react";
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import MoreTaxesEditor from '../aggrid-cell-editors/more-taxes-editor'
import ActionsRenderer from '../aggrid-cell-renderers/actions-renderer'
import MyDataMoreTaxesTypeEditor from '../aggrid-cell-editors/myDATA-more-taxes-type-editor'
import SimpleObjectRenderer from '../aggrid-cell-renderers/simple-object-renderer'
import AmountEditor from '../aggrid-cell-editors/amount-editor'
import DoubleValRenderer from '../aggrid-cell-renderers/doubleval-renderer'
import { useEditableGrid } from './useEditableGrid';

const EMPTY_ROW = {
    myDataMoreTax: { value: undefined, label: undefined },
    type: { value: undefined, label: undefined }
};

const isValid = (data) =>
    data.type && data.type.value != undefined && data.type.value != "" &&
    data.myDataMoreTax && data.myDataMoreTax.label != undefined && data.myDataMoreTax.label != "";

const MoreTaxesGrid = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const { gridApi, hasError, rowStyle, getRowStyle, initGrid, addRow, checkErrors, getItems, setItems } = useEditableGrid({
        isValid,
        emptyRow: EMPTY_ROW,
        globalRows: true,
    });

    const onGridReady = (params) => {
        initGrid(params, props.moreTaxes);
    };

    const onCellValueChanged = (params) => {
        const nodeIndex = params.node.rowIndex;
        const countDisplayedRows = params.api.getDisplayedRowCount() - 1;
        if (nodeIndex == countDisplayedRows && params.newValue != undefined) {
            addRow(params.api);
            params.api.redrawRows();
        }

        if (params.column.colId == "amount") {
            props.reCalculateTotals();
        }

        checkErrors(params.api);
    };

    useImperativeHandle(ref, () => ({
        transformData(data, moreTaxesTypesOptions, myDataMoreTaxesOptions) {
            const newData = [];
            data.forEach(el => {
                const cats = [];
                const moreTaxesOptions = [];
                myDataMoreTaxesOptions.forEach(element => {
                    cats.push({
                        value: element.value,
                        label: element.type == "DEDUCTION" ? element.label : t("TAX_" + element.value),
                        type: element.type,
                        key: element.key,
                        uid: element.uid,
                        amountValue: element.amountValue
                    });
                });
                moreTaxesTypesOptions.forEach(element => {
                    moreTaxesOptions.push({ value: element.value, label: t(element.label) });
                });
                const groupTaxes = cats.reduce((r, a) => {
                    r[a.type] = [...r[a.type] || [], a];
                    return r;
                }, {});

                if (el.deduction) {
                    el.myDataMoreTax = {
                        type: "DEDUCTION",
                        id: el.deduction.id,
                        value: el.deduction.id,
                        uid: el.deduction.id,
                        subtracted: el.deduction.subtracted,
                        label: el.deduction.name
                    };
                }

                const typeTaxes = groupTaxes[el.myDataMoreTax.type];
                const new_type_options = [];

                if (typeTaxes) {
                    typeTaxes.forEach(element => {
                        new_type_options.push({
                            value: element.type == "DEDUCTION" ? element.value : "TAX_" + element.value,
                            label: element.type == "DEDUCTION" ? element.label : t("TAX_" + element.value),
                            amountValue: element.amountValue,
                            uid: element.uid,
                            subtracted: element.type == "DEDUCTION" ? element.subtracted : null,
                            id: element.type == "DEDUCTION" ? element.value : null
                        });
                    });
                }

                newData.push({
                    id: el.id,
                    type: { value: el.myDataMoreTax.type, label: t(el.myDataMoreTax.type) },
                    myDataMoreTax: {
                        id: el.myDataMoreTax.type == "DEDUCTION" ? el.myDataMoreTax.value : null,
                        uid: el.myDataMoreTax.uid,
                        amountValue: el.myDataMoreTax.amountValue,
                        value: el.myDataMoreTax.type == "DEDUCTION" ? el.myDataMoreTax.value : "TAX_" + el.myDataMoreTax.uid,
                        label: el.myDataMoreTax.type == "DEDUCTION" ? el.myDataMoreTax.label : t("TAX_" + el.myDataMoreTax.uid),
                        type: el.myDataMoreTax.type,
                        subtracted: el.myDataMoreTax.type == "DEDUCTION" ? el.myDataMoreTax.subtracted : null,
                    },
                    myDataMoreTaxesTypes: moreTaxesOptions,
                    myDataMoreTaxes: new_type_options,
                    amount: el.amount,
                });
            });
            return newData;
        },
        getData() {
            return getItems().map(element => {
                element.myDataMoreTax.type = element.type.value;
                return { id: element.id, myDataMoreTax: element.myDataMoreTax, amount: element.amount };
            });
        },
        clearData() {
            const data = [{ product: { value: undefined, label: undefined }, description: undefined, quantity: undefined, price: undefined, measurementUnit: undefined }];
            gridApi.setRowData(data);
            gridApi.gos.gridOptions.rowData = data;
            props.reCalculateTotals();
        },
        setData(data) {
            setItems(data);
        },
        hasError() {
            return hasError;
        }
    }));

    const deleteRowById = (rowIndex, api) => {
        const rowData = [];
        api.forEachNode(node => rowData.push(node.data));
        rowData.splice(rowIndex, 1);
        api.setRowData(rowData);
        props.reCalculateTotals();
    };

    return (
        <div className="row">
            <small className="salesTaxesGridInfo">{t("mandatorySaleTaxesFieldsError")}</small>
            <div className="col-md-12">
                <div className="ag-theme-alpine editableSmGrid salesTaxesGrid" style={{ width: '100%' }}>
                    <AgGridReact
                        onGridReady={onGridReady}
                        rowStyle={rowStyle}
                        getRowStyle={getRowStyle}
                        style={{ width: '100%', height: '300px' }}
                        onCellValueChanged={onCellValueChanged}
                        stopEditingWhenCellsLoseFocus={true}
                        borders={true}
                        suppressRowTransform={true}
                        singleClickEdit={true}
                        skipHeaderOnAutoSize={true}
                        defaultColDef={{
                            flex: 1,
                            resizable: true,
                            wrapText: true,
                            autoHeight: true,
                            suppressMovable: true
                        }}
                        columnDefs={[
                            { field: "myDataMoreTaxes", hide: true },
                            { field: "type", flex: 3, cellEditorPopup: true, minWidth: 200, headerName: t("myDataMoreTaxesType"), editable: true, cellRenderer: SimpleObjectRenderer, cellEditor: MyDataMoreTaxesTypeEditor, cellRendererParams: { moreTaxesOptions: props.moreTaxesOptions, options: props.moreTaxesTypesOptions } },
                            { field: "myDataMoreTax", flex: 3, minWidth: 200, headerName: t("tax"), editable: true, cellRenderer: SimpleObjectRenderer, cellEditor: MoreTaxesEditor, cellRendererParams: { rowTotals: props.rowTotals } },
                            { field: "amount", flex: 1, minWidth: 100, headerName: t("Amount"), editable: true, cellRenderer: DoubleValRenderer },
                            { field: "name", flex: 1, minWidth: 100, headerName: t("Actions"), cellRenderer: ActionsRenderer, cellRendererParams: { deleteRowById: deleteRowById } },
                        ]}
                    >
                    </AgGridReact>
                </div>
            </div>
            <div><br /></div>
        </div>
    );
});
export default MoreTaxesGrid;
